import React from "react";
import clsx from "clsx";

interface SkeletonLoaderProps {
  variant: "list" | "feed" | "page" | "profile" | "card" | "table" | "stats";
  count?: number;
  className?: string;
  showShimmer?: boolean;
}

const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({
  variant,
  count = 3,
  className = "",
  showShimmer = true,
}) => {
  // Base animation class that can be applied conditionally
  const animationClass = showShimmer ? "animate-pulse" : "";

  const renderList = () =>
    Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        className={clsx(
          "flex items-center space-x-4 mb-4 p-3 rounded-lg",
          animationClass
        )}
      >
        <div className="w-12 h-12 bg-gray-300 rounded-full flex-shrink-0" />
        <div className="flex-1 space-y-3">
          <div className="h-4 bg-gray-300 rounded w-3/4" />
          <div className="h-3 bg-gray-200 rounded w-1/2" />
          <div className="flex items-center space-x-2 pt-1">
            <div className="h-2 bg-gray-200 rounded w-8" />
            <div className="h-2 bg-gray-200 rounded w-8" />
            <div className="h-2 bg-gray-200 rounded w-8" />
          </div>
        </div>
        <div className="flex-shrink-0">
          <div className="h-6 w-6 bg-gray-200 rounded" />
        </div>
      </div>
    ));

  const renderFeed = () =>
    Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        className={clsx(
          "mb-8 border border-gray-200 rounded-xl overflow-hidden",
          animationClass
        )}
      >
        {/* Header */}
        <div className="flex items-center space-x-3 p-4 border-b border-gray-100">
          <div className="w-10 h-10 bg-gray-300 rounded-full" />
          <div className="flex-1">
            <div className="h-4 bg-gray-300 rounded w-1/3 mb-2" />
            <div className="h-3 bg-gray-200 rounded w-1/4" />
          </div>
          <div className="w-6 h-6 bg-gray-200 rounded-full" />
        </div>

        {/* Content */}
        <div className="h-64 bg-gray-300" />

        {/* Actions */}
        <div className="p-4 space-y-3">
          <div className="flex space-x-4">
            <div className="h-6 w-6 bg-gray-200 rounded" />
            <div className="h-6 w-6 bg-gray-200 rounded" />
            <div className="h-6 w-6 bg-gray-200 rounded" />
          </div>
          <div className="h-4 bg-gray-300 rounded w-32" />
          <div className="h-3 bg-gray-200 rounded w-full" />
        </div>
      </div>
    ));

  const renderPage = () => (
    <div className={clsx("space-y-6", animationClass)}>
      {/* Header */}
      <div className="space-y-4">
        <div className="h-8 bg-gray-300 rounded w-1/3" />
        <div className="flex items-center space-x-2">
          <div className="h-4 bg-gray-200 rounded w-16" />
          <div className="h-4 bg-gray-200 rounded w-16" />
          <div className="h-4 bg-gray-200 rounded w-16" />
        </div>
      </div>

      {/* Hero section */}
      <div className="h-64 bg-gray-300 rounded-lg" />

      {/* Content sections */}
      <div className="space-y-3">
        <div className="h-6 bg-gray-300 rounded w-1/4" />
        <div className="h-4 bg-gray-200 rounded w-full" />
        <div className="h-4 bg-gray-200 rounded w-full" />
        <div className="h-4 bg-gray-200 rounded w-3/4" />
      </div>

      <div className="space-y-3">
        <div className="h-6 bg-gray-300 rounded w-1/4" />
        <div className="h-4 bg-gray-200 rounded w-full" />
        <div className="h-4 bg-gray-200 rounded w-full" />
        <div className="h-4 bg-gray-200 rounded w-1/2" />
      </div>

      {/* Footer navigation */}
      <div className="flex justify-between pt-4 border-t border-gray-200">
        <div className="h-10 bg-gray-200 rounded w-1/4" />
        <div className="h-10 bg-gray-200 rounded w-1/4" />
      </div>
    </div>
  );

  const renderProfile = () => (
    <div
      className={clsx(
        "flex flex-col items-center text-center space-y-6",
        animationClass
      )}
    >
      {/* Cover image */}
      <div className="w-full h-32 bg-gray-300 rounded-lg" />

      {/* Avatar (overlapping the cover) */}
      <div className="w-24 h-24 bg-gray-400 rounded-full border-4 border-white -mt-12" />

      {/* Profile info */}
      <div className="space-y-4 w-full max-w-md">
        <div className="h-6 bg-gray-300 rounded w-1/3 mx-auto" />
        <div className="h-4 bg-gray-200 rounded w-2/3 mx-auto" />

        {/* Stats */}
        <div className="flex justify-center space-x-6 pt-2">
          <div className="space-y-1">
            <div className="h-5 bg-gray-300 rounded w-12 mx-auto" />
            <div className="h-3 bg-gray-200 rounded w-16" />
          </div>
          <div className="space-y-1">
            <div className="h-5 bg-gray-300 rounded w-12 mx-auto" />
            <div className="h-3 bg-gray-200 rounded w-16" />
          </div>
          <div className="space-y-1">
            <div className="h-5 bg-gray-300 rounded w-12 mx-auto" />
            <div className="h-3 bg-gray-200 rounded w-16" />
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex justify-center space-x-3 pt-2">
          <div className="h-10 bg-gray-300 rounded w-1/4" />
          <div className="h-10 bg-gray-200 rounded w-1/4" />
        </div>
      </div>
    </div>
  );

  const renderCard = () =>
    Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        className={clsx(
          "rounded-xl overflow-hidden border border-gray-200 mb-4",
          animationClass
        )}
      >
        {/* Card image */}
        <div className="h-48 bg-gray-300" />

        {/* Card content */}
        <div className="p-4 space-y-3">
          <div className="h-5 bg-gray-300 rounded w-2/3" />
          <div className="h-4 bg-gray-200 rounded w-full" />
          <div className="h-4 bg-gray-200 rounded w-1/2" />

          {/* Footer */}
          <div className="flex items-center justify-between pt-3">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gray-300 rounded-full" />
              <div className="h-3 bg-gray-200 rounded w-20" />
            </div>
            <div className="h-8 bg-gray-300 rounded w-20" />
          </div>
        </div>
      </div>
    ));

  const renderTable = () => (
    <div
      className={clsx(
        "rounded-lg border border-gray-200 overflow-hidden",
        animationClass
      )}
    >
      {/* Table header */}
      <div className="flex border-b border-gray-200 bg-gray-50 p-4">
        <div className="w-1/5 pr-3">
          <div className="h-4 bg-gray-300 rounded w-3/4" />
        </div>
        <div className="w-1/5 px-3">
          <div className="h-4 bg-gray-300 rounded w-3/4" />
        </div>
        <div className="w-1/5 px-3">
          <div className="h-4 bg-gray-300 rounded w-3/4" />
        </div>
        <div className="w-1/5 px-3">
          <div className="h-4 bg-gray-300 rounded w-3/4" />
        </div>
        <div className="w-1/5 pl-3">
          <div className="h-4 bg-gray-300 rounded w-3/4" />
        </div>
      </div>

      {/* Table rows */}
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex border-b border-gray-200 p-4">
          <div className="w-1/5 pr-3">
            <div className="h-4 bg-gray-200 rounded w-3/4" />
          </div>
          <div className="w-1/5 px-3">
            <div className="h-4 bg-gray-200 rounded w-1/2" />
          </div>
          <div className="w-1/5 px-3">
            <div className="h-4 bg-gray-200 rounded w-2/3" />
          </div>
          <div className="w-1/5 px-3">
            <div className="h-4 bg-gray-200 rounded w-1/2" />
          </div>
          <div className="w-1/5 pl-3">
            <div className="h-4 bg-gray-200 rounded w-1/4" />
          </div>
        </div>
      ))}
    </div>
  );

  const renderStats = () => (
    <div
      className={clsx(
        "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4",
        animationClass
      )}
    >
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="border border-gray-200 rounded-lg p-5 bg-white">
          <div className="flex justify-between items-start mb-4">
            <div className="space-y-1">
              <div className="h-3 bg-gray-200 rounded w-16" />
              <div className="h-6 bg-gray-300 rounded w-24" />
            </div>
            <div className="w-10 h-10 bg-gray-200 rounded-lg" />
          </div>

          <div className="h-10 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gray-300 rounded-full"
              style={{ width: `${Math.random() * 90 + 10}%` }}
            />
          </div>

          <div className="flex justify-between items-center mt-2">
            <div className="h-3 bg-gray-200 rounded w-16" />
            <div className="h-3 bg-gray-200 rounded w-12" />
          </div>
        </div>
      ))}
    </div>
  );

  const variantMap: Record<string, React.ReactNode> = {
    list: renderList(),
    feed: renderFeed(),
    page: renderPage(),
    profile: renderProfile(),
    card: renderCard(),
    table: renderTable(),
    stats: renderStats(),
  };

  return (
    <div className={clsx("text-left", className)}>
      {variantMap[variant] || (
        <div className="text-gray-500">Unknown skeleton variant: {variant}</div>
      )}
    </div>
  );
};

export default SkeletonLoader;
